// src/components/sections/FinancingSummary.jsx
import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  isOwnedDeal,
  isInPlaceLoanFinancing,
  isZeroDebtFinancing,
} from "../../utils/dealPurpose";

export default function FinancingSummary({
  analysis,
  currentProperty,
  equityAmount,
  scenarioType,
  financing = {},
  refinancingNotPossible = false,
}) {
  const { t } = useLanguage();
  const formatMoney = (value) => {
    if (value === null || value === undefined) return "—";
    return new Intl.NumberFormat("fr-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    return `${
      new Intl.NumberFormat("fr-CA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(value))
    }%`;
  };

  const owned = isOwnedDeal(currentProperty) || analysis?.isOwnedDeal;
  const inPlaceLoan = isInPlaceLoanFinancing(financing.financingType);
  const zeroDebt = isZeroDebtFinancing(financing.financingType);
  const isRefinancing =
    scenarioType === undefined || scenarioType !== "initialFinancing";
  const isRenewal = scenarioType === "renewal";

  const purchaseLabel = owned
    ? t("financingSummary.propertyValue")
    : isRefinancing
      ? t("financingSummary.propertyValue")
      : t("financingSummary.purchasePrice");
  const downPaymentLabel = owned
    ? t("financingSummary.netEquity")
    : isRefinancing
      ? t("financingSummary.trappedEquity")
      : t("financingSummary.downPayment");
  const feesLabel =
    scenarioType === "optimization"
      ? t("financingSummary.financingAndWorksFees")
      : isRefinancing
      ? t("financingSummary.financingFees")
      : t("financingSummary.acquisitionFees");

  const financingTypeLabels = {
    conventional: t("financingSummary.conventional"),
    cmhc: t("financingSummary.cmhc"),
    cmhc_aph: t("financingSummary.cmhc_aph"),
    private: t("financingSummary.private"),
    assumption: t("financingSummary.assumption"),
    existing: t("financingSummary.existing"),
    none: t("financingSummary.none"),
  };

  const loanAmountLabel = () => {
    if (isRenewal) return t("financingSummary.loanBalance");
    if (financing.financingType === "assumption") return t("financingSummary.assumedLoan");
    if (financing.financingType === "existing") return t("financingSummary.existingLoan");
    if (zeroDebt) return t("financingSummary.loanBalance");
    return t("financingSummary.maxLoan");
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-4">{t('financingSummary.title')}</h3>
      <div className="space-y-3">
        {financing.financingType && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.financingType')}:</span>
            <span className="font-medium">
              {financingTypeLabels[financing.financingType] ||
                financing.financingType}
            </span>
          </div>
        )}
        {financing.financingType === "cmhc_aph" &&
          financing.aphPoints &&
          !isRenewal && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.aphPoints')}:</span>
            <span className="font-medium">{financing.aphPoints}</span>
          </div>
        )}
        {inPlaceLoan
          ? (financing.mortgageRate != null && financing.mortgageRate !== '' ? (
            <div className="flex justify-between gap-2 min-w-0">
              <span className="text-gray-600">{t('financingSummary.contractualRate')}:</span>
              <span className="font-medium">{formatPercent(financing.mortgageRate)}</span>
            </div>
          ) : null)
          : !zeroDebt && ((financing.qualificationRate != null && financing.qualificationRate !== '') || (financing.mortgageRate != null && financing.mortgageRate !== '') ? (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{isRefinancing ? t('financingSummary.newInterestRate') : t('financingSummary.qualificationRate')}:</span>
            <span className="font-medium">
              {formatPercent(financing.qualificationRate ?? financing.mortgageRate)}
            </span>
          </div>
        ) : null)}
        {financing.amortization && !inPlaceLoan && !zeroDebt && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">
              {isRenewal ? t('financingSummary.remainingAmortization') : t('financingSummary.amortization')}:
            </span>
            <span className="font-medium">{financing.amortization} ans</span>
          </div>
        )}
        {financing.debtCoverageRatio && !isRenewal && !inPlaceLoan && !zeroDebt && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.debtCoverageRatio')}:</span>
            <span className="font-medium">{financing.debtCoverageRatio}</span>
          </div>
        )}
        {financing.financingCapRate && !isRenewal && !inPlaceLoan && financing.financingType !== 'private' && !zeroDebt && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.financingCapRate')}:</span>
            <span className="font-medium">{formatPercent(financing.financingCapRate)}</span>
          </div>
        )}
        {financing.financingType === "private" && financing.ltvRatio && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.ltvRatio')}:</span>
            <span className="font-medium">{formatPercent(financing.ltvRatio)}</span>
          </div>
        )}
        {inPlaceLoan &&
          analysis?.assumptionDerivedRemainingMonths != null && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.assumptionRemainingMonths')}:</span>
            <span className="font-medium">
              {new Intl.NumberFormat(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
              }).format(analysis.assumptionDerivedRemainingMonths)}
            </span>
          </div>
        )}
        {inPlaceLoan && financing.assumptionTermEnd && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.assumptionTermEnd')}:</span>
            <span className="font-medium">{financing.assumptionTermEnd}</span>
          </div>
        )}
        {(inPlaceLoan || zeroDebt) && analysis?.loanValueRatio != null && !owned && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.ltvRatio')}:</span>
            <span className="font-medium">{formatPercent(analysis.loanValueRatio)}</span>
          </div>
        )}
        {financing.financingType === "private" && analysis?.originationFee > 0 && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.originationFee')}:</span>
            <span className="font-medium">{formatMoney(analysis.originationFee)}</span>
          </div>
        )}
        {scenarioType !== "optimization" && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{purchaseLabel}:</span>
            <span className="font-medium">
              {formatMoney(parseFloat(currentProperty?.purchasePrice) || 0)}
            </span>
          </div>
        )}
        {!isRenewal && !inPlaceLoan && !zeroDebt && (
          <div className="flex justify-between gap-2 min-w-0">
          <span className="text-gray-600">{t('financingSummary.borrowingValue')}:</span>
            <span className="font-medium">{formatMoney(analysis?.economicValue)}</span>
          </div>
        )}
        <div className="flex justify-between gap-2 min-w-0">
          <span className="text-gray-600">
            {loanAmountLabel()}
            :
          </span>
          <span className="font-medium">
            {formatMoney(
              isRenewal ? analysis?.totalLoanAmount : analysis?.maxLoanAmount
            )}
          </span>
        </div>
        {!isRenewal && analysis?.cmhcPremium > 0 && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.cmhcPremium')}:</span>
            <span className="font-medium">{formatMoney(analysis?.cmhcPremium)}</span>
          </div>
        )}
        {!isRenewal && analysis?.cmhcPremium > 0 && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{t('financingSummary.totalLoanWithPremium')}:</span>
            <span className="font-medium">{formatMoney(analysis?.totalLoanAmount)}</span>
          </div>
        )}
        <div className="flex justify-between gap-2 min-w-0">
          <span className="text-gray-600">{downPaymentLabel}:</span>
          <span className="font-medium">
            {formatMoney(owned ? (analysis?.equity ?? analysis?.downPayment) : analysis?.downPayment)}
          </span>
        </div>
        {!isRenewal && !owned && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">{feesLabel}:</span>
            <span className="font-medium">{formatMoney(analysis?.acquisitionCosts)}</span>
          </div>
        )}
        {!isRenewal && scenarioType !== "refinancing" && scenarioType !== "optimization" && (
          equityAmount !== undefined ? (
            <div className="flex justify-between gap-2 min-w-0 border-t pt-3">
              <span className="text-gray-600 font-semibold">{t('financingSummary.equityWithdrawal')}:</span>
              <span
                className={`font-bold text-lg ${
                  equityAmount >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {formatMoney(equityAmount)}
              </span>
            </div>
          ) : (
            <div className="flex justify-between gap-2 min-w-0 border-t pt-3">
              <span className="text-gray-600 font-semibold">
                {owned ? t('financingSummary.netEquity') : t('financingSummary.totalInvestment')}:
              </span>
              <span className="font-bold text-lg">
                {formatMoney(analysis?.totalInvestment)}
              </span>
            </div>
          )
        )}
        {scenarioType === "refinancing" && equityAmount !== undefined && (
          <div className="flex justify-between gap-2 min-w-0 border-t pt-3">
            <span className="text-gray-600 font-semibold">{t('financingSummary.equityWithdrawal')}:</span>
            <span
              className={`font-bold text-lg ${
                equityAmount >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {formatMoney(equityAmount)}
            </span>
          </div>
        )}
        {scenarioType === "optimization" && equityAmount !== undefined && (
          <div className="flex justify-between gap-2 min-w-0 border-t pt-3">
            <span className="text-gray-600 font-semibold">{t('financingSummary.equityWithdrawal')}:</span>
            <span
              className={`font-bold text-lg ${
                equityAmount >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {formatMoney(equityAmount)}
            </span>
          </div>
        )}
        {refinancingNotPossible && (
          <p className="text-red-600 font-medium text-center mt-3 pt-3 border-t border-gray-200">
            {t('scenarioForm.notPossible')}
          </p>
        )}
        {!zeroDebt && (
          <div className="flex justify-between gap-2 min-w-0">
            <span className="text-gray-600">
              {financing.financingType === "private"
                ? t('financingSummary.monthlyPaymentInterestOnly') + ':'
                : t('financingSummary.monthlyPayment') + ':'}
            </span>
            <span className="font-medium">{formatMoney(analysis?.monthlyPayment)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
